/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { AdvancePatientSearch } from '../Modals'
import {
  Header,
  type HeaderHipaaView,
  type HeaderLocationOption,
  type HeaderNotificationItem,
  type HeaderNotificationPriority,
  type HeaderUserMenuItem,
} from '@enamelai/design-system'

import { logout } from '@store/actions/auth.action'
import {
  markAllNotificationsReadThunk,
  markNotificationReadThunk,
  markNotificationUnreadThunk,
} from '@store/actions/notification.action'

import './NavigationStyle.css'
import { PatientWithInsurance } from '@dataTypes/appointment.type'
import { Patient } from '@dataTypes/PatientInfo.types'
import {
  Notification,
  NotificationCategoryLabel,
  NotificationPriority,
} from '@dataTypes/notification.types'
import { searchPatientsByName } from '@services/api/patient.api'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import {
  setHIPAAView,
  setActiveSidePanel,
  closeSidePanel,
} from '@store/reducers/uiSlice.reducer'
import { getFullName } from '@utils/fullname.util'
import Avatar from '@components/common/Avatar/Avatar.component'
import { isProviderSeatRole } from '@constants/Providers'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { setSelectedClinic } from '@store/reducers/clinic.reducer'
import useNotifications from '@components/pages/Notifications/useNotifications'
import { createNewTwilioDevice } from '@utils/device.twilio'
import { useSignedBlobUrl } from '@hooks/useSignedBlobUrl'
import { fetchAllContacts } from '@store/actions'
import { navigateToPatientProfilePreservingReturn } from '@utils/patientProfileNavigation.util'
import {
  getNotificationCategory,
  navigateToNotificationTarget,
} from '@utils/notification.util'
import enamelaiLogo from '@assets/images/Enamelai_logo.png'

import { logger } from '@utils/safeLogger'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'

const PATIENT_PROFILE_PATH_PATTERN = /^\/patient-profile\/[^/]+/

const DEFAULT_CLINIC_LOGO_URL = '/clinic.png'

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const mapNotificationPriority = (
  priority: NotificationPriority,
): HeaderNotificationPriority => {
  if (priority === NotificationPriority.CRITICAL) {
    return 'critical'
  }

  if (priority === NotificationPriority.URGENT) {
    return 'urgent'
  }

  return 'low'
}

const mapNotificationToHeaderItem = (
  notification: Notification,
): HeaderNotificationItem => {
  const category =
    notification.category ?? getNotificationCategory(notification)

  return {
    id: notification.notification_id ?? notification.id,
    title: notification.title,
    message: notification.message,
    date: notification.date,
    time: notification.time,
    priority: mapNotificationPriority(notification.priority),
    category: NotificationCategoryLabel[category],
    isRead: notification.is_read,
    actionButtonText: notification.actionButtonText,
  }
}

function Navigation(): React.ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const isPatientProfilePage = PATIENT_PROFILE_PATH_PATTERN.test(
    location.pathname,
  )
  const dispatch = useAppDispatch()
  const { setLoading } = useGlobalLoadingActions()
  const notificationsHook = useNotifications()
  const reduxNotifications = useAppSelector((state) => state.notification.items)
  const unreadCount = useAppSelector(
    (state) => state.notification.counts.unread,
  )
  const [showSearchPatModal, setShowSearchPatModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedName, setDebouncedName] = useState('')
  const [patients, setPatients] = useState<PatientWithInsurance[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const searchRequestIdRef = useRef(0)
  const headerRef = useRef<HTMLDivElement>(null)

  const clinicData = useAppSelector((state) => state.clinic.data)
  const staffAssignedClinicIds = useAppSelector(
    (state) => state.clinic.staffAssignedClinicIds,
  )
  const clinicList = useMemo(() => {
    if (staffAssignedClinicIds === null) return clinicData
    const allowed = new Set(staffAssignedClinicIds)
    return clinicData.filter((c) => allowed.has(c.clinic_id))
  }, [clinicData, staffAssignedClinicIds])
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  /** While staff scope is loading, Redux may still point at a non-assigned clinic; keep the toggle aligned with the dropdown list. */
  const clinicShownInNav = useMemo(() => {
    if (!selectedClinic) return null
    if (staffAssignedClinicIds === null) return selectedClinic
    const match = clinicList.find(
      (c) => c.clinic_id === selectedClinic.clinic_id,
    )
    return match ?? clinicList[0] ?? null
  }, [selectedClinic, staffAssignedClinicIds, clinicList])
  const clinicLogoSrc = useSignedBlobUrl(
    clinicShownInNav?.logo_url,
    DEFAULT_CLINIC_LOGO_URL,
  )
  const contacts = useAppSelector((state) => state.contacts.data)
  const isHIPAAView = useAppSelector((state) => state.ui.isHIPAAView)
  const loggedInUser = useAppSelector(
    (state) => state.chatInternal.loggedInUser,
  )
  const storedUsername = (
    JSON.parse(localStorage.getItem('user') ?? '{}') as { username?: string }
  ).username
  const navUserDisplayName = loggedInUser
    ? `${
        isProviderSeatRole(
          loggedInUser.provider_profile?.work_role ?? undefined,
        )
          ? 'Dr. '
          : ''
      }${getFullName(loggedInUser)}`
    : storedUsername || 'User'
  const navUserPhoto = loggedInUser?.provider_profile?.photo ?? ''

  const headerLocations: HeaderLocationOption[] = useMemo(
    () =>
      clinicList.map((clinic) => ({
        id: clinic.clinic_id,
        name: clinic.clinic_name,
        address: clinic.address,
      })),
    [clinicList],
  )

  // Prefer the Redux list for the header so the panel is not tied to the
  // notifications-page date/tab filters from useNotifications.
  const notificationSource = useMemo(() => {
    if (reduxNotifications.length > 0) {
      return reduxNotifications
    }

    return notificationsHook.criticalNotifications
      .concat(notificationsHook.urgentNotifications)
      .concat(notificationsHook.lowNotifications)
  }, [
    reduxNotifications,
    notificationsHook.criticalNotifications,
    notificationsHook.urgentNotifications,
    notificationsHook.lowNotifications,
  ])

  const headerNotifications: HeaderNotificationItem[] = useMemo(
    () => notificationSource.map(mapNotificationToHeaderItem),
    [notificationSource],
  )

  const notificationById = useMemo(() => {
    const map = new Map<string, Notification>()
    for (const notification of notificationSource) {
      map.set(notification.notification_id ?? notification.id, notification)
    }
    return map
  }, [notificationSource])

  const userMenuItems: HeaderUserMenuItem[] = useMemo(
    () => [
      {
        id: 'account',
        label: 'Account',
        onSelect: () => navigate('/accounts'),
      },
      {
        id: 'logout',
        label: 'Log Out',
        onSelect: () => {
          setLoading(false)
          dispatch(logout())
        },
      },
    ],
    [dispatch, navigate, setLoading],
  )

  const fetchPatients = async (name: string): Promise<void> => {
    const requestId = ++searchRequestIdRef.current
    setIsSearching(true)
    try {
      const response = await searchPatientsByName(name)
      if (requestId !== searchRequestIdRef.current) return
      setPatients(
        response.statusCode === 200 ? (response.data?.patients ?? []) : [],
      )
    } catch (error) {
      if (requestId !== searchRequestIdRef.current) return
      logger.error('Error fetching patients:', error)
      setPatients([])
    } finally {
      if (requestId === searchRequestIdRef.current) setIsSearching(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedName.trim()) {
        void fetchPatients(debouncedName.trim())
      } else {
        searchRequestIdRef.current += 1
        setIsSearching(false)
        setPatients([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [debouncedName])

  useEffect(() => {
    if (!isSearchOpen) return

    const handlePointerDown = (event: MouseEvent): void => {
      if (!headerRef.current?.contains(event.target as Node)) {
        setIsSearchOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsSearchOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSearchOpen])

  const patientNameHandleChange = (value: string): void => {
    setSearchTerm(value)
    setDebouncedName(value)
    setIsSearchOpen(true)
  }

  const clearSearch = (): void => {
    setSearchTerm('')
    setDebouncedName('')
    setPatients([])
    setIsSearchOpen(false)
  }

  const selectPatientFromSearch = (
    patient: Patient | PatientWithInsurance,
  ): void => {
    const contact = contacts?.find((c) => c.patient_id === patient.patient_id)
    if (isPatientProfilePage) {
      dispatch(closeSidePanel())
      dispatch(setSelectedPatient({ patient: patient as Patient, contact }))
      navigateToPatientProfilePreservingReturn(
        navigate,
        patient.patient_id,
        location.state,
      )
    } else {
      dispatch(setActiveSidePanel('main'))
      dispatch(setSelectedPatient({ patient: patient as Patient, contact }))
    }
    clearSearch()
  }

  const handlePatientSelect = (
    e: React.MouseEvent,
    patient: PatientWithInsurance,
  ): void => {
    e.preventDefault()
    selectPatientFromSearch(patient)
  }

  const toggleSearchPatModal = (): void => {
    setShowSearchPatModal((prev) => !prev)
  }

  const openAdvancedSearch = (): void => {
    setIsSearchOpen(false)
    setShowSearchPatModal(true)
  }

  const handleClinicSelect = (clinicId: string): void => {
    const clinic = clinicList.find((item) => item.clinic_id === clinicId)
    if (!clinic) return

    dispatch(setSelectedClinic(clinic))
    dispatch(fetchAllContacts())
    dispatch(closeSidePanel())
    createNewTwilioDevice(dispatch)
  }

  const highlightText = (text: string, highlight: string): React.ReactNode => {
    if (!highlight.trim()) return text
    const parts = text.split(new RegExp(`(${escapeRegExp(highlight)})`, 'gi'))
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} className="highlight">
          {part}
        </span>
      ) : (
        part
      ),
    )
  }

  const handleHipaaViewChange = (view: HeaderHipaaView): void => {
    dispatch(setHIPAAView(view === 'view'))
  }

  const handleMarkAllNotificationsRead = (): void => {
    void dispatch(markAllNotificationsReadThunk())
  }

  const handleNotificationToggleRead = (
    notificationId: string,
    isRead: boolean,
  ): void => {
    const notification = notificationById.get(notificationId)
    const serverId = notification?.notification_id ?? notificationId
    void dispatch(
      isRead
        ? markNotificationReadThunk(serverId)
        : markNotificationUnreadThunk(serverId),
    )
  }

  const handleNotificationAction = (notificationId: string): void => {
    const notification = notificationById.get(notificationId)
    if (!notification) return
    navigateToNotificationTarget(navigate, notification)
  }

  const handleViewAllNotifications = (): void => {
    navigate('/notifications')
  }

  return (
    <>
      <div ref={headerRef} className="ds-app-header sticky-top">
        <Header
          logo={
            <img src={enamelaiLogo} width={38} height={38} alt="ENAMELAI" />
          }
          showSearch
          searchValue={searchTerm}
          searchPlaceholder="Search"
          onSearchChange={patientNameHandleChange}
          onSearchClear={clearSearch}
          showHipaaToggle
          hipaaView={isHIPAAView ? 'view' : 'hide'}
          onHipaaViewChange={handleHipaaViewChange}
          showLocation
          locationName={clinicShownInNav?.clinic_name || 'Clinic'}
          locationImageSrc={clinicLogoSrc}
          locations={headerLocations}
          selectedLocationId={clinicShownInNav?.clinic_id}
          onLocationSelect={handleClinicSelect}
          showNotifications
          hasNotifications={unreadCount > 0}
          notifications={headerNotifications}
          onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
          onViewAllNotifications={handleViewAllNotifications}
          onExpandNotifications={handleViewAllNotifications}
          onNotificationToggleRead={handleNotificationToggleRead}
          onNotificationAction={handleNotificationAction}
          showUser
          userName={navUserDisplayName}
          userEmail={storedUsername}
          userImageSrc={navUserPhoto || undefined}
          userMenuItems={userMenuItems}
        />
        {isSearchOpen && searchTerm.trim() && (
          <div className="list-group search-results ds-app-header__search-results">
            {patients.map((patient) => (
              <a
                href="#"
                key={patient.patient_id}
                className="list-group-item list-group-item-action d-flex gap-2 py-2 align-items-center"
                onClick={(e) => handlePatientSelect(e, patient)}
              >
                <Avatar
                  source={
                    patient?.photo_file?.file_path ?? patient?.photo ?? ''
                  }
                  alt={`${patient.first_name} ${patient.last_name}`}
                  width={32}
                  height={32}
                  className="rounded flex-shrink-0"
                />
                <div className="min-w-0">
                  <h6 className="mb-0">
                    {highlightText(
                      getFullName(patient, true, isHIPAAView),
                      searchTerm,
                    )}
                  </h6>
                  <p className="mb-0 opacity-75 address-text">
                    <i className="ti ti-map-pin address-icon me-1"></i>
                    {`${patient?.address} ${patient?.city}, ${patient?.state} ${patient?.zip}`}
                  </p>
                </div>
              </a>
            ))}
            {patients.length === 0 && (
              <div className="list-group-item ds-app-header__search-status">
                {isSearching ? 'Searching…' : 'No patients found'}
              </div>
            )}
            <button
              type="button"
              className="list-group-item list-group-item-action ds-app-header__search-advanced"
              onClick={openAdvancedSearch}
            >
              <i className="mgc mgc_filter_2_line me-1"></i>
              Advanced search
            </button>
          </div>
        )}
      </div>
      <AdvancePatientSearch
        isOpen={showSearchPatModal}
        toggleModal={toggleSearchPatModal}
        onSelectPatient={selectPatientFromSearch}
      />
    </>
  )
}

export default Navigation
